// src/pages/GalleryPage.tsx
import React, { useEffect } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import CustomLoader from "../../components/placeholders/CustomLoader";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import SearchBar from "../../components/UI/SearchBar";
import EventCard from "../../components/cards/EventCard";
import { useEvents } from "../../context/EventContext";
import EmptyState from "../../components/placeholders/EmptyState";
import { ImageIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { extractApiErrorMessage } from "../../helpers/utils";

const GalleryPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    isLoading,
    hasInitialized,
    setHasInitialized,
    events,
    filteredEvents,
    searchQuery,
    setSearchQuery,
    listEvents,
  } = useEvents();

  useEffect(() => {
    if (hasInitialized.gallery) {
      return;
    }

    let isMounted = true;

    listEvents()
      .catch((error) => {
        toast({
          title: t("pages.gallery.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      })
      .finally(() => {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, gallery: true }));
        }
      });

    return () => {
      isMounted = false;
    };
  }, [hasInitialized.gallery, listEvents, setHasInitialized, t, toast]);

  const hasNoData = events.length === 0;
  const hasNoSearchResults = filteredEvents.length === 0 && !hasNoData;

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <SearchBar
        placeholder={String(t("pages.gallery.searchPlaceholder"))}
        value={searchQuery}
        onSearch={setSearchQuery}
        mode="local"
      />
      {isLoading || !hasInitialized.gallery ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={ImageIcon}
          title={t("pages.gallery.emptyTitle")}
          description={t("pages.gallery.emptyDescription")}
          mt={7}
        />
      ) : hasNoSearchResults ? (
        <EmptyState
          icon={ImageIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
          mt={7}
        />
      ) : (
        <VStack spacing={CARD_SPACING} mt={7} w="full">
          {filteredEvents.map((event) => (
            <EventCard
              key={event.eventID}
              eventID={event.eventID}
              title={event.title}
              description={event.description}
              photoCount={event.numberOfPhotos}
              videoCount={event.numberOfVideos}
              thumbnailUrl={event.thumbnailUrl}
              onClick={() => {
                window.open(event.link, "_blank", "noopener,noreferrer");
              }}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default GalleryPage;
